import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { setAnnotationStatus } from '@/api/annotations'
import { confidenceBand, AUTO_ACCEPT_THRESHOLD } from '@/api/proposals'
import { keys } from '@/api/keys'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { Slider } from '@/components/ui/slider'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'
import { useEditorStore, type ProposalView } from '@/stores/editorStore'

import { acceptProposalItems, rejectRemaining, reviewStateOf, sureItems, type ProposalItem } from './applyProposal'
import { describeSaveError, ensureSaved } from './useAutosave'
import { kindLabel, useVocab } from './vocab'

/**
 * The suggestions bar above the canvas (§9.4) — for an AI run started from the editor, a
 * `?proposal=` deep link, or the review queue.
 *
 * Everything here is **local** until Save: "Accept" puts the suggestions into the picture as one
 * undoable step (Ctrl+Z takes them out again) and the header then says "Unsaved changes"; the
 * next Save writes the picture and tells the server which suggestions were used. "Drop" just
 * hides one. "Reject all" with nothing accepted yet dismisses the whole set on the server (it
 * does not touch the picture).
 *
 * Plain buttons first — **Accept sure ones**, **Accept all**, **Reject all** — for the person whose
 * job is "the fox looks wrong", not "reconcile this diff"; the per-item Keep / Drop list, the
 * certainty slider and the view switch sit underneath.
 */

export interface ProposalReviewBarProps {
  /** Called after suggestions were taken into the picture (locally), with how many. */
  onAccepted?: (taken: number) => void
  /** Review-queue flow: "Looks fine" takes the sure ones, saves, marks the picture checked, then calls this. */
  onNext?: () => void
  /** "Fix on picture": take the sure ones, then open the guided editor. */
  onFixOnPicture?: () => void
  /** The guided editor's version: no view switch, no slider. */
  simple?: boolean
  className?: string
}

/** One shared empty list, so store selectors return a stable value. */
const NONE: readonly string[] = []

const COLLECTION_WORDS: Record<ProposalItem['collection'], [string, string]> = {
  region: ['shape', 'shapes'],
  anchor: ['spot', 'spots'],
  portal: ['way in or out', 'ways in and out'],
  path: ['path', 'paths'],
}

function summarise(items: readonly ProposalItem[]): string {
  const counts = new Map<ProposalItem['collection'], number>()
  for (const i of items) counts.set(i.collection, (counts.get(i.collection) ?? 0) + 1)
  const parts: string[] = []
  for (const c of ['region', 'anchor', 'portal', 'path'] as const) {
    const n = counts.get(c)
    if (n) parts.push(`${n} ${COLLECTION_WORDS[c][n === 1 ? 0 : 1]}`)
  }
  return parts.join(', ')
}

/** Plain-language notes about how the suggestions were made (mode, source, run flags). */
export function proposalNotices(p: Pick<ProposalView, 'mode' | 'source' | 'flags'>): string[] {
  const out: string[] = []
  const flags = p.flags ?? []
  const has = (f: string) => flags.includes(f)
  if (p.source === 'mock' || p.mode === 'mock') out.push('Test suggestions: the AI is not connected, so these are made-up examples.')
  else if (p.mode === 'heuristic_only' || p.source === 'heuristic' || has('ai_unavailable') || has('heuristic_only'))
    out.push('The AI was not available, so the studio measured the picture itself. These are simpler — check them carefully.')
  if (flags.some((f) => f.startsWith('ai_paused_until'))) out.push('The AI is taking a break, so part of this was measured by the studio.')
  if (has('spots_fallback')) out.push('Spots and ways in and out were measured by the studio, not the AI.')
  if (has('budget_stopped')) out.push('The AI stopped early to stay within the spending limit.')
  if (has('critique_skipped')) out.push('The AI could not double-check its work this time.')
  if (has('low_confidence')) out.push('The AI was not very sure about this picture.')
  if (has('coord_frame_suspect')) out.push('Some shapes may be a little out of place — check where they sit.')
  if (has('layout_empty')) out.push('The AI could not find ground, water or sky in this picture.')
  return out
}

export function ProposalReviewBar({ onAccepted, onNext, onFixOnPicture, simple = false, className }: ProposalReviewBarProps) {
  const vocab = useVocab()
  const qc = useQueryClient()
  const proposal = useEditorStore((s) => s.proposal)
  const doc = useEditorStore((s) => s.doc)
  const dirty = useEditorStore((s) => s.dirty)
  const accepted = useEditorStore((s) => (s.proposal ? (s.proposalAccepted[String(s.proposal.id)] ?? NONE) : NONE))
  const droppedIds = useEditorStore((s) => (s.proposal ? (s.proposalDropped[String(s.proposal.id)] ?? NONE) : NONE))
  const setProposalView = useEditorStore((s) => s.setProposalView)
  const setProposalThreshold = useEditorStore((s) => s.setProposalThreshold)
  const setProposalFocus = useEditorStore((s) => s.setProposalFocus)
  const setProposal = useEditorStore((s) => s.setProposal)
  const dropProposalItems = useEditorStore((s) => s.dropProposalItems)
  const undropProposalItems = useEditorStore((s) => s.undropProposalItems)
  const [busy, setBusy] = useState<string | null>(null)
  const [showFindings, setShowFindings] = useState(false)

  const review = useMemo(() => reviewStateOf(doc, proposal, droppedIds, accepted), [doc, proposal, droppedIds, accepted])

  if (!proposal) return null

  const threshold = proposal.threshold ?? AUTO_ACCEPT_THRESHOLD
  const items = review.items
  const sure = sureItems(items, threshold)
  const notices = proposalNotices(proposal)
  const findings = proposal.findings ?? []
  const openFindings = findings.filter((f) => !f.fixed && f.severity !== 'info')
  const fixedCount = findings.filter((f) => f.fixed).length
  const label = (i: ProposalItem) => i.label?.trim() || kindLabel(vocab, i.kind, i.domain)
  const sourceBadge = proposal.source === 'mock' || proposal.mode === 'mock' ? 'Test suggestions' : proposal.source === 'heuristic' || proposal.mode === 'heuristic_only' ? 'Measured suggestions' : 'AI suggestions'

  const accept = async (ids: string[], tag: string): Promise<boolean> => {
    if (!ids.length) return true
    setBusy(tag)
    try {
      const out = await acceptProposalItems(ids)
      if (!out.ok) {
        if (out.error) toast.error('Could not add the suggestions', { description: out.error })
        return false
      }
      onAccepted?.(out.taken)
      if (out.taken > 0) toast.success(`Added ${out.taken} ${out.taken === 1 ? 'suggestion' : 'suggestions'} to the picture`, { description: 'Press Save to keep them, or Undo to take them out.' })
      return true
    } finally {
      setBusy(null)
    }
  }

  const rejectAll = async () => {
    setBusy('reject')
    try {
      const out = await rejectRemaining(items.map((i) => i.id))
      if (!out.ok) toast.error('Could not dismiss the suggestions', { description: out.error })
      else void qc.invalidateQueries({ queryKey: keys.proposals.all() })
    } finally {
      setBusy(null)
    }
  }

  const looksFine = async () => {
    const ok = await accept(sure.map((i) => i.id), 'fine')
    if (!ok) return
    setBusy('fine')
    try {
      const saved = await ensureSaved(qc)
      if (!saved) {
        toast.error('Could not save the picture', { description: describeSaveError(useEditorStore.getState().saveError) ?? undefined })
        return
      }
      const meta = useEditorStore.getState().meta
      if (meta) {
        try {
          const env = await setAnnotationStatus(meta.uid, 'reviewed')
          useEditorStore.getState().applyServerMeta(env)
        } catch (err) {
          const e = err as { message?: string; details?: { lints?: { message: string; fix_hint?: string }[] } }
          const first = e.details?.lints?.[0]
          toast.error(first ? first.message : 'Saved, but it could not be marked as checked yet.', first?.fix_hint ? { description: first.fix_hint } : {})
          return
        }
      }
      void qc.invalidateQueries({ queryKey: keys.proposals.all() })
      onNext?.()
    } finally {
      setBusy(null)
    }
  }

  const disabled = busy !== null
  const takenCount = accepted.length
  const summary = items.length
    ? `${items.length} ${items.length === 1 ? 'suggestion' : 'suggestions'} to check: ${summarise(items)}.`
    : takenCount
      ? 'All suggestions handled.'
      : review.settled
        ? 'Nothing new — the picture already matches these suggestions.'
        : 'Nothing to review.'

  return (
    // The shell bounds its height (a long list must scroll inside the bar, never push the page):
    // the expert layout wraps it in a capped scroller, Simple mode passes the cap as `className`.
    <section aria-label="Suggestions" className={cn('flex shrink-0 flex-col gap-2 border-b border-line bg-paper px-4 py-3', className)} data-testid="proposal-review">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Badge tone="info">{sourceBadge}</Badge>
        {proposal.model && <span className="text-xs text-ink-2">Model: {proposal.provider ? `${proposal.provider} · ` : ''}{proposal.model}</span>}
        <p className="min-w-0 flex-1 text-sm text-ink">{summary}</p>

        <div className="flex flex-wrap items-center gap-2">
          {items.length ? (
            <>
              <Button variant="primary" size="sm" disabled={disabled || sure.length === 0} loading={busy === 'sure'} onClick={() => void accept(sure.map((i) => i.id), 'sure')}>
                Accept sure ones ({sure.length})
              </Button>
              <Button variant="secondary" size="sm" disabled={disabled} loading={busy === 'all'} onClick={() => void accept(items.map((i) => i.id), 'all')}>
                Accept all
              </Button>
              <Button variant="ghost" size="sm" disabled={disabled} loading={busy === 'reject'} onClick={() => void rejectAll()}>
                {takenCount ? 'Dismiss the rest' : 'Reject all'}
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="sm" disabled={disabled} onClick={() => setProposal(null)}>
              Close
            </Button>
          )}
          {onNext ? (
            <Button variant="outline" size="sm" disabled={disabled} loading={busy === 'fine'} onClick={() => void looksFine()}>
              Looks fine — save
            </Button>
          ) : null}
          {onFixOnPicture && items.length ? (
            <Button
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() =>
                void accept(sure.map((i) => i.id), 'fix').then((ok) => {
                  if (ok) onFixOnPicture()
                })
              }
            >
              Fix on picture
            </Button>
          ) : null}
        </div>
      </div>

      {notices.length || (takenCount > 0 && dirty) || fixedCount ? (
        <ul className="space-y-0.5 text-xs text-ink-2" aria-label="About these suggestions">
          {notices.map((n) => (
            <li key={n}>{n}</li>
          ))}
          {fixedCount ? <li>The AI checked its own work and fixed {fixedCount} {fixedCount === 1 ? 'mistake' : 'mistakes'}.</li> : null}
          {takenCount > 0 && dirty ? <li className="font-medium text-ink">Suggestions you accepted are on the picture now — press Save to keep them.</li> : null}
        </ul>
      ) : null}

      {!simple && items.length ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-1" role="group" aria-label="How to show the suggestions">
            {(['off', 'overlay', 'split', 'swipe'] as const).map((v) => (
              <Chip key={v} selected={proposal.view === v} onClick={() => setProposalView(v)}>
                {v === 'off' ? 'Hide' : v === 'overlay' ? 'On top' : v === 'split' ? 'Side by side' : 'Swipe'}
              </Chip>
            ))}
          </div>

          <label className="flex min-w-[220px] items-center gap-2 text-xs text-ink-2">
            <span className="whitespace-nowrap">“Sure” means at least {Math.round(threshold * 100)}%</span>
            <Slider
              value={[threshold]}
              min={0}
              max={1}
              step={0.05}
              onValueChange={(v) => setProposalThreshold(v[0] ?? AUTO_ACCEPT_THRESHOLD)}
              aria-label="How sure a suggestion must be"
            />
          </label>
        </div>
      ) : null}

      {items.length ? (
        <ul className="flex max-h-32 flex-wrap gap-2 overflow-y-auto overscroll-contain" aria-label="Each suggestion">
          {items.map((item) => {
            const band = confidenceBand(item.confidence)
            const name = label(item)
            return (
              <li
                key={item.id}
                className="flex items-center gap-1 rounded-md border border-line px-2 py-1 text-xs"
                onMouseEnter={() => setProposalFocus(item.id)}
                onMouseLeave={() => setProposalFocus(null)}
                onFocus={() => setProposalFocus(item.id)}
                onBlur={() => setProposalFocus(null)}
              >
                <Badge tone={band === 'high' ? 'ok' : band === 'medium' ? 'warn' : 'neutral'} title="How sure the AI is">
                  {Math.round(item.confidence * 100)}%
                </Badge>
                <span className="font-medium">{name}</span>
                <span className="text-ink-2">{item.what === 'new' ? 'new' : 'change'}</span>
                <Button variant="ghost" size="sm" disabled={disabled} loading={busy === `item:${item.id}`} onClick={() => void accept([item.id], `item:${item.id}`)} aria-label={`Keep ${name}`}>
                  Keep
                </Button>
                <Button variant="ghost" size="sm" disabled={disabled} onClick={() => dropProposalItems([item.id])} aria-label={`Drop ${name}`}>
                  Drop
                </Button>
              </li>
            )
          })}
        </ul>
      ) : null}

      {review.dropped.length ? (
        <p className="text-xs text-ink-2">
          {review.dropped.length} dropped.{' '}
          <Button variant="link" size="sm" className="h-auto text-xs" onClick={() => undropProposalItems(review.dropped.map((i) => i.id))}>
            Bring them back
          </Button>
        </p>
      ) : null}

      {review.lockedSkipped ? (
        <p className="text-xs text-ink-2">
          {review.lockedSkipped} {review.lockedSkipped === 1 ? 'shape was' : 'shapes were'} left exactly as you made {review.lockedSkipped === 1 ? 'it' : 'them'}.
        </p>
      ) : null}

      {openFindings.length ? (
        <div className="text-xs">
          <Button variant="link" size="sm" className="h-auto px-0 text-xs" aria-expanded={showFindings} onClick={() => setShowFindings((v) => !v)}>
            {showFindings ? 'Hide' : 'Show'} {openFindings.length} {openFindings.length === 1 ? 'thing' : 'things'} the AI wants you to check
          </Button>
          {showFindings ? (
            <ul className="mt-1 max-h-28 list-disc space-y-0.5 overflow-y-auto pl-5 text-ink-2">
              {openFindings.map((f, i) => (
                <li key={`${f.code}:${f.ref ?? ''}:${i}`}>{f.msg}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
